import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ActionMethod, ActionOrigin, MODBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import * as FileConstants from 'folder-items/FileConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as ComplexPolymerActions from 'modbio/complex-polymers/ComplexPolymerActions';
import {
  COMPLEX_POLYMER_DISPLAY_NAMES,
  ComplexPolymerConnection,
  ComplexPolymerType,
  ResolvedAttachmentInput,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {
  ComplexPolymerSchemaConfigGraphQL,
  generateEntityAssociationsFromTags,
  isOligoDuplexConfigGraphQLType,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import OligoDuplexOffsetField from 'modbio/complex-polymers/tabs/OligoDuplexOffsetField';
import notifications from 'notifications';
import {
  CreateBioentityTagInputsQuery,
  CreateBioentityTagInputsQueryVariables,
} from 'registry/create-bioentity/__generated/CreateBioentityTagInputsQuery';
import CreateBioentityTagInputs, {
  CREATE_BIOENTITY_TAG_INPUTS_QUERY,
} from 'registry/create-bioentity/CreateBioentityTagInputs';
import {getTagsToSet} from 'schemas/tags/TagUtil';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import {InlineBannerProps} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Modal from 'ui-library/modals/Modal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {
  ComplexPolymerNameAndFolderInputs,
  QueryingComplexPolymerFieldInputs,
} from './ComplexPolymerCreateInputs';
import CreateComplexPolymerAttachConjugatePane from './CreateComplexPolymerAttachConjugatePane';
import {
  CreateComplexPolymerModalContext,
  CreateComplexPolymerModalContextPayload,
} from './CreateComplexPolymerModalContext';
import CreateComplexPolymerOligoPreview from './CreateComplexPolymerOligoPreview';
import {
  CreateComplexPolymerSchemaTag,
  getBackboneAndConjugatesTags,
  getEntityIdsAndRefsFromTags,
  getInitialComplexPolymerTags,
  preprocessConnectionInputsOnUpdate,
} from './CreateComplexPolymerUtil';

export enum ComplexPolymerCreateStep {
  SELECT_ENTITIES = 'SELECT_ENTITIES',
  CONFIGURE_ATTACHMENTS = 'CONFIGURE_ATTACHMENTS',
}

export interface QueryingCreateComplexPolymerModalProps {
  initialFolderId: string | null;
  schemaId: string;
  config: ComplexPolymerSchemaConfigGraphQL;
  complexPolymerType: ComplexPolymerType;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function QueryingCreateComplexPolymerModal({
  initialFolderId,
  schemaId,
  config,
  complexPolymerType,
  onRequestClose,
  onRequestSubmit,
}: QueryingCreateComplexPolymerModalProps): JSX.Element {
  return (
    <BenchlingQuery<CreateBioentityTagInputsQuery, CreateBioentityTagInputsQueryVariables>
      query={CREATE_BIOENTITY_TAG_INPUTS_QUERY}
      variables={{tagSchemaId: schemaId}}
      fetchPolicy="cache-and-network"
      renderLoading={() => <HorizontalThrobber />}
    >
      {(data) => {
        const schema = data.tagSchema;
        assert(!isInaccessible(schema), 'Expected schema to be accessible');
        const initialComplexPolymerTags = getInitialComplexPolymerTags(schema.allFieldsJson, config);

        return (
          <CreateComplexPolymerModal
            initialFolderId={initialFolderId}
            schemaId={schemaId}
            config={config}
            complexPolymerType={complexPolymerType}
            initialComplexPolymerTags={initialComplexPolymerTags}
            onRequestClose={onRequestClose}
            onRequestSubmit={onRequestSubmit}
          />
        );
      }}
    </BenchlingQuery>
  );
}

interface CreateComplexPolymerModalProps {
  initialFolderId: string | null;
  schemaId: string;
  config: ComplexPolymerSchemaConfigGraphQL;
  complexPolymerType: ComplexPolymerType;
  initialComplexPolymerTags: Array<FileConstants.SchemaTag>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

function CreateComplexPolymerModal({
  initialFolderId,
  schemaId,
  config,
  complexPolymerType,
  initialComplexPolymerTags,
  onRequestClose,
  onRequestSubmit,
}: CreateComplexPolymerModalProps): JSX.Element {
  const [name, setName] = useState<string>('');
  const [folderId, setFolderId] = useState(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );

  const [tags, setTags] = useState<Array<FileConstants.SchemaTag>>([]);
  const [complexPolymerTags, setComplexPolymerTags] =
    useState<Array<CreateComplexPolymerSchemaTag>>(initialComplexPolymerTags);
  const [posting, setPosting] = useState(false);
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);

  const isOligoDuplex = complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX;
  const [offset, setOffset] = useState<number | null>(0);

  const requiredComplexPolymerTags = complexPolymerTags.filter((tag) => tag.tagSchemaField.isRequired);
  const disabled =
    name.trim().length === 0 ||
    folderId == null ||
    posting ||
    requiredComplexPolymerTags.some((tag) => !tag.targetFiles || tag.targetFiles.length === 0) ||
    (isOligoDuplex && offset == null);

  const [step, setStep] = useState<ComplexPolymerCreateStep>(ComplexPolymerCreateStep.SELECT_ENTITIES);
  const [connectionInputs, setConnectionInputs] = useState<Array<ComplexPolymerConnection>>([]);
  const [resolvedAttachmentInputs, setResolvedAttachmentInputs] = useState<Array<ResolvedAttachmentInput>>(
    []
  );

  const [bannerProps, setBannerProps] = useState<null | InlineBannerProps>(null);
  const getContextValue = (): CreateComplexPolymerModalContextPayload => ({setBannerProps});

  async function createComplexPolymer(): Promise<void> {
    assert(folderId != null, 'Unexpected creating complex polymer with null folderId');
    assert(!isOligoDuplex || offset != null, 'Expect oligo duplex to have non-null offset');

    setPosting(true);
    try {
      await withLoadingScreen(
        ComplexPolymerActions.createComplexPolymer(
          name,
          folderId,
          schemaId,
          getTagsToSet(tags),
          complexPolymerTags,
          config,
          studySearchItems.map((item) => item.id),
          connectionInputs,
          resolvedAttachmentInputs,
          isOligoDuplex ? offset! : undefined
        ),
        `Creating ${COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType]}...`
      );
      FolderDAOCache.invalidateFolderContents(folderId);
      onRequestSubmit();
    } catch (err) {
      setPosting(false);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  }

  const studiesInputs = BenchlingConfig.STUDIES_ENABLE ? (
    <Row>
      <Col sm={12}>
        <FormGroup>
          <FieldLabel label="Studies" />
          <StudySearchInput
            onChange={(studySearchItems: Array<StudySearchItem>) => {
              setStudySearchItems(studySearchItems);
            }}
            shouldAllowMultiple={true}
            selectedItems={studySearchItems}
          />
        </FormGroup>
      </Col>
    </Row>
  ) : null;

  const tagInputs = (
    <CreateBioentityTagInputs
      labelStyle={LabelStyle.LABEL}
      tags={tags}
      onChange={setTags}
      tagSchemaId={schemaId}
    />
  );

  const modalSizeProps =
    step === ComplexPolymerCreateStep.SELECT_ENTITIES
      ? {large: true}
      : {
          fullWidth: true,
          fullHeight: true,
        };

  return (
    <Modal
      title={`Create ${COMPLEX_POLYMER_DISPLAY_NAMES[complexPolymerType]}`}
      buttons={[{disabled, submit: true, text: 'Create'}]}
      bodyPadded={step === ComplexPolymerCreateStep.SELECT_ENTITIES}
      onRequestClose={onRequestClose}
      onRequestSubmit={createComplexPolymer}
      bannerProps={bannerProps}
      {...modalSizeProps}
    >
      {step === ComplexPolymerCreateStep.SELECT_ENTITIES && (
        <ComplexPolymerNameAndFolderInputs
          name={name}
          setName={setName}
          folderId={folderId}
          setFolderId={setFolderId}
        />
      )}
      <CreateComplexPolymerModalContext.Provider value={getContextValue()}>
        <CreateComplexPolymerModalContents
          complexPolymerType={complexPolymerType}
          complexPolymerSchemaConfig={config}
          complexPolymerComponents={complexPolymerTags}
          complexPolymerConnections={connectionInputs}
          resolvedAttachmentInputs={resolvedAttachmentInputs}
          oligoDuplexOffset={offset}
          tagInputs={tagInputs}
          studiesInputs={studiesInputs}
          step={step}
          setResolvedAttachmentInputs={setResolvedAttachmentInputs}
          setComplexPolymerConnections={setConnectionInputs}
          setComplexPolymerComponents={setComplexPolymerTags}
          setOffset={setOffset}
          setStep={setStep}
        />
      </CreateComplexPolymerModalContext.Provider>
    </Modal>
  );
}

export interface CreateComplexPolymerModalContentsProps {
  complexPolymerType: ComplexPolymerType;
  complexPolymerSchemaConfig: ComplexPolymerSchemaConfigGraphQL;
  complexPolymerComponents: Array<FileConstants.SchemaTag>;
  complexPolymerConnections: Array<ComplexPolymerConnection>;
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>;
  oligoDuplexOffset: number | null;
  step: ComplexPolymerCreateStep;
  tagInputs?: JSX.Element | null;
  studiesInputs?: JSX.Element | null;
  setStep: (step: ComplexPolymerCreateStep) => void;
  setResolvedAttachmentInputs: (attachments: Array<ResolvedAttachmentInput>) => void;
  setComplexPolymerConnections: (connections: Array<ComplexPolymerConnection>) => void;
  setComplexPolymerComponents: (tags: Array<FileConstants.SchemaTag>) => void;
  setOffset: (offset: number) => void;
}

export function CreateComplexPolymerModalContents(
  props: CreateComplexPolymerModalContentsProps
): JSX.Element {
  const [attachedConjugateRefs, setAttachedConjugateRefs] = useState<Array<string>>([]);
  const [currentConjugateRefIdx, unsafeSetCurrentConjugateRefIdx] = useState(0);

  const entityAssociations = generateEntityAssociationsFromTags(props.complexPolymerComponents ?? []);
  const {backboneTags, conjugatesTags} = getBackboneAndConjugatesTags(
    props.complexPolymerSchemaConfig,
    props.complexPolymerComponents ?? []
  );
  const {ids: conjugateIds, refs: conjugateRefs} = getEntityIdsAndRefsFromTags(conjugatesTags);
  const {ids: backboneIds, refs: backboneRefs} = getEntityIdsAndRefsFromTags(backboneTags);
  const selectedEntityIds = [...backboneIds, ...conjugateIds];

  function setCurrentConjugateRefIdx(idx: number): void {
    unsafeSetCurrentConjugateRefIdx(Math.min(idx, conjugateRefs.length - 1));
  }

  const {complexPolymerConnections, setComplexPolymerConnections} = props;

  useEffect(() => {
    const refs = new Set(Object.values(entityAssociations).flat());

    const preprocessedConnectionInputs = preprocessConnectionInputsOnUpdate(complexPolymerConnections, refs);
    if (preprocessedConnectionInputs.length !== complexPolymerConnections.length) {
      setComplexPolymerConnections(preprocessedConnectionInputs);
    }

    // Only consider a conjugate to be attached if it is the target side of the connection input.
    // - Connections to backbones will always have the conjugate as the target.
    // - If we connect a conjugate to a conjugate, we have to pick one side to consider "attached".
    const preprocessedAttachedRefsSet: Set<string> = new Set(
      preprocessedConnectionInputs.map((connection) => connection.target.polymer_ref)
    );
    const preprocessedAttachedRefs = Array.from(preprocessedAttachedRefsSet);
    if (preprocessedAttachedRefs.length !== attachedConjugateRefs.length) {
      setAttachedConjugateRefs(preprocessedAttachedRefs);
    }
  }, [complexPolymerConnections, setComplexPolymerConnections, entityAssociations, attachedConjugateRefs]);

  return props.step === ComplexPolymerCreateStep.SELECT_ENTITIES ? (
    <>
      <SelectComplexPolymerEntitiesStep
        {...props}
        setStep={props.setStep}
        conjugateRefs={conjugateRefs}
        backboneRefs={backboneRefs}
      />
    </>
  ) : (
    <ConfigureComplexPolymerAttachmentsStep
      complexPolymerType={props.complexPolymerType}
      selectedEntityIds={selectedEntityIds}
      selectedConjugateIds={conjugateIds}
      attachedConjugateRefs={attachedConjugateRefs}
      currentConjugateRefIdx={currentConjugateRefIdx}
      conjugateRefs={conjugateRefs}
      connectionInputs={props.complexPolymerConnections}
      resolvedAttachmentInputs={props.resolvedAttachmentInputs}
      entityAssociations={entityAssociations}
      setStep={props.setStep}
      setResolvedAttachmentInputs={props.setResolvedAttachmentInputs}
      setConnectionInputs={props.setComplexPolymerConnections}
      setCurrentConjugateRefIdx={setCurrentConjugateRefIdx}
      setAttachedConjugateRefs={setAttachedConjugateRefs}
    />
  );
}

interface ConfigureAttachmentsButtonProps {
  disabled: boolean;
  setStep: (step: ComplexPolymerCreateStep) => void;
}

function ConfigureAttachmentsButton({disabled, setStep}: ConfigureAttachmentsButtonProps): JSX.Element {
  return (
    <div className={css(styles.configureAttachmentsButton)}>
      <Button
        icon="link"
        text="Configure attachments"
        disabled={disabled}
        buttonStyle={ButtonStyle.SECONDARY_WHITE}
        size={ButtonSize.SMALL}
        onClick={() => {
          trackEvent(MODBIO_ANALYTICS_ACTIONS.OPENED_CONFIGURE_ATTACHMENTS_FOR_COMPLEX_POLYMER_CREATE, {
            action_origin: ActionOrigin.MODBIO_COMPLEX_POLYMERS,
            action_method: ActionMethod.BUTTON_CLICK,
          });
          setStep(ComplexPolymerCreateStep.CONFIGURE_ATTACHMENTS);
        }}
        dataTestAttrs={{
          component: 'CreateComplexPolymerModal',
          element: 'button',
          key: 'configureAttachments',
        }}
      />
    </div>
  );
}

interface ComplexPolymersSelectEntitiesPaneProps {
  complexPolymerSchemaConfig: ComplexPolymerSchemaConfigGraphQL;
  complexPolymerComponents: Array<FileConstants.SchemaTag>;
  oligoDuplexOffset: number | null;
  conjugateRefs: Array<string>;
  backboneRefs: Array<string>;
  complexPolymerType: ComplexPolymerType;
  tagInputs?: JSX.Element | null;
  setStep: (step: ComplexPolymerCreateStep) => void;
  setComplexPolymerComponents: (tags: Array<FileConstants.SchemaTag>) => void;
  setOffset: (offset: number) => void;
}

function ComplexPolymersSelectEntitiesPane({
  complexPolymerSchemaConfig,
  complexPolymerComponents,
  oligoDuplexOffset,
  tagInputs,
  setComplexPolymerComponents,
  setOffset,
}: ComplexPolymersSelectEntitiesPaneProps): JSX.Element {
  return (
    <>
      <Row>
        <Col sm={12}>
          <FormGroup>
            {tagInputs}
            <QueryingComplexPolymerFieldInputs
              complexPolymerConfig={complexPolymerSchemaConfig}
              complexPolymerTags={complexPolymerComponents}
              setComplexPolymerTags={setComplexPolymerComponents}
            />
          </FormGroup>
        </Col>
      </Row>
      {isOligoDuplexConfigGraphQLType(complexPolymerSchemaConfig) && (
        <Row className={css(styles.offsetInput)}>
          <Col sm={12}>
            <OligoDuplexOffsetField offset={oligoDuplexOffset} setOffset={setOffset} isEditable={true} />
          </Col>
        </Row>
      )}
      <CreateComplexPolymerOligoPreview
        complexPolymerConfig={complexPolymerSchemaConfig}
        complexPolymerTags={complexPolymerComponents}
        offset={oligoDuplexOffset ?? 0}
      />
    </>
  );
}

interface SelectComplexPolymerEntitiesStepProps {
  complexPolymerSchemaConfig: ComplexPolymerSchemaConfigGraphQL;
  complexPolymerComponents: Array<FileConstants.SchemaTag>;
  oligoDuplexOffset: number | null;
  conjugateRefs: Array<string>;
  backboneRefs: Array<string>;
  complexPolymerType: ComplexPolymerType;
  studiesInputs?: JSX.Element | null;
  tagInputs?: JSX.Element | null;
  setStep: (step: ComplexPolymerCreateStep) => void;
  setComplexPolymerComponents: (tags: Array<FileConstants.SchemaTag>) => void;
  setOffset: (offset: number) => void;
}

export function SelectComplexPolymerEntitiesStep(props: SelectComplexPolymerEntitiesStepProps): JSX.Element {
  return (
    <>
      <ComplexPolymersSelectEntitiesPane {...props} />
      {props.studiesInputs}
      <ConfigureAttachmentsButton
        disabled={
          props.conjugateRefs.length === 0 ||
          props.backboneRefs.length < (props.complexPolymerType === ComplexPolymerType.OLIGO_DUPLEX ? 2 : 1)
        }
        setStep={props.setStep}
      />
    </>
  );
}

interface ConfigureComplexPolymerAttachmentsStepProps {
  complexPolymerType: ComplexPolymerType;
  setStep: (step: ComplexPolymerCreateStep) => void;
  selectedEntityIds: Array<string>;
  selectedConjugateIds: Array<string>;
  currentConjugateRefIdx: number;
  setCurrentConjugateRefIdx: (idx: number) => void;
  conjugateRefs: Array<string>;
  connectionInputs: Array<ComplexPolymerConnection>;
  setConnectionInputs: (connectionInputs: Array<ComplexPolymerConnection>) => void;
  attachedConjugateRefs: Array<string>;
  setAttachedConjugateRefs: (attachedConjugateRefs: Array<string>) => void;
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>;
  setResolvedAttachmentInputs: (resolvedAttachmentInputs: Array<ResolvedAttachmentInput>) => void;
  entityAssociations: Record<string, Array<string>>;
}

export function ConfigureComplexPolymerAttachmentsStep({
  setStep,
  complexPolymerType,
  selectedEntityIds,
  selectedConjugateIds,
  conjugateRefs,
  currentConjugateRefIdx,
  setCurrentConjugateRefIdx,
  connectionInputs,
  setConnectionInputs,
  attachedConjugateRefs,
  setAttachedConjugateRefs,
  resolvedAttachmentInputs,
  setResolvedAttachmentInputs,
  entityAssociations,
}: ConfigureComplexPolymerAttachmentsStepProps): JSX.Element {
  return (
    <div
      className={css(styles.configureAttachmentsStep)}
      {...getTestProps({component: 'ConfigureComplexPolymerAttachmentsStep', element: 'root'})}
    >
      <div className={css(styles.configureAttachmentsHeader)}>
        <span className={css(styles.configureAttachmentHeaderText)}>
          <BenchlingIcon
            className={css(styles.backButton)}
            icon="chevron-left"
            onClick={() => {
              setStep(ComplexPolymerCreateStep.SELECT_ENTITIES);
            }}
            dataTestAttrs={{
              component: 'ConfigureComplexPolymerAttachmentsStep',
              element: 'button',
              key: 'back',
            }}
          />
          Configure attachments
        </span>
      </div>
      <CreateComplexPolymerAttachConjugatePane
        complexPolymerType={complexPolymerType}
        selectedEntityIds={selectedEntityIds}
        selectedConjugateIds={selectedConjugateIds}
        conjugateSelectionParams={{
          selectedConjugateRefs: conjugateRefs,
          currentConjugateRefIdx,
          setCurrentConjugateRefIdx,
        }}
        connectionInputs={connectionInputs}
        setConnectionInputs={setConnectionInputs}
        attachedConjugateRefs={attachedConjugateRefs}
        setAttachedConjugateRefs={setAttachedConjugateRefs}
        resolvedAttachmentInputs={resolvedAttachmentInputs}
        setResolvedAttachmentInputs={setResolvedAttachmentInputs}
        entityAssociations={entityAssociations}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  offsetInput: {
    marginBottom: '15px',
  },
  configureAttachmentsButton: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  backButton: {
    color: Color.GRAY_500,
    height: '14px',
    paddingRight: '10px',
    ':hover': {
      cursor: 'pointer',
    },
  },
  configureAttachmentsStep: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  configureAttachmentHeaderText: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '16px',
  },
  configureAttachmentsHeader: {
    padding: '15px',
    borderBottom: `1px solid ${Color.GRAY_200}`,
  },
});
